import React, { useState } from 'react';
import { CheckCircleIcon, ArrowRightIcon, TargetIcon, ShieldIcon, CrownIcon, ChevronDownIcon, ChevronUpIcon, CheckIcon, XIcon, CopyIcon } from 'lucide-react';
const MaleTypeIdentifier = () => {
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [showQuiz, setShowQuiz] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [quizResult, setQuizResult] = useState<string | null>(null);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const maleTypes = [{
    id: 'hunter',
    title: 'El Cazador',
    icon: <TargetIcon size={24} className="text-red-500" data-id="element-767" />,
    percentage: '35%',
    description: 'Competitivo, directo y orientado a logros. Valora independencia y desafíos.',
    characteristics: ['Valora su libertad por encima de todo', 'Competitivo y orientado a objetivos', 'Le gusta perseguir y conquistar', 'Puede parecer distante emocionalmente', 'Responde bien a desafíos sutiles'],
    detailedProfile: {
      personalityTraits: ['Independiente y autónomo', 'Competitivo en todos los ámbitos de la vida', 'Orientado a la acción y resultados', 'Disfruta del proceso de conquista', 'Valora el respeto por encima del afecto', 'Necesita espacio y libertad', 'Puede ser impaciente y directo', 'Toma decisiones rápidas basadas en lógica'],
      emotionalPatterns: ['Reservado con sus emociones profundas', 'Expresa afecto a través de acciones, no palabras', 'Puede parecer frío cuando está procesando emociones', 'Se aleja cuando se siente presionado emocionalmente', 'Valora la fortaleza emocional en su pareja', 'Respeta a quienes mantienen su independencia emocional'],
      relationshipBehaviors: ['Persigue activamente cuando está interesado', 'Disfruta de la "caza" y el desafío', 'Puede perder interés si la conquista es demasiado fácil', 'Prefiere relaciones donde mantiene cierta autonomía', 'Puede tener dificultad con el compromiso a largo plazo', 'Valora a parejas que tienen su propia vida e intereses'],
      communicationStyle: ['Directo y conciso, valora la eficiencia', 'Prefiere conversaciones con propósito', 'Puede ser impaciente con charlas emocionales extensas', 'Responde mejor a comunicación clara y sin rodeos', 'Aprecia el espacio para procesar información', 'Puede ser selectivo con lo que comparte']
    },
    strategy: 'Usa disparadores de escasez y misterio. Muestra que tienes una vida independiente e interesante. Nunca parezcas desesperada o excesivamente disponible.',
    dosAndDonts: {
      dos: ['Mantén tu independencia y vida propia', 'Crea misterio y cierta imprevisibilidad', 'Demuestra confianza y fortaleza emocional', 'Ofrece desafíos sutiles que despiertan su interés', 'Reconoce sus logros genuinamente', 'Respeta su necesidad de espacio', 'Mantén conversaciones interesantes y estimulantes', 'Muestra interés en sus pasiones y proyectos'],
      donts: ['Perseguirlo o mostrarte demasiado disponible', 'Hacer demasiadas preguntas personales demasiado pronto', 'Mostrar inseguridad o necesidad excesiva de validación', 'Intentar controlarlo o limitar su libertad', 'Presionar para compromisos o definiciones prematuras', 'Mostrar celos posesivos o comportamiento controlador', 'Enviar mensajes constantes o exigir respuestas inmediatas', 'Criticarlo públicamente o desafiar su autonomía']
    },
    idealMessages: [{
      type: 'Escasez',
      content: 'Hola! Tenía ganas de contarte algo divertido que pasó, pero estoy saliendo ahora. ¿Hablamos otro día?',
      explanation: 'Demuestra que tienes una vida ocupada y no estás esperando por él'
    }, {
      type: 'Desafío',
      content: 'Me sorprendió lo que dijiste el otro día sobre [tema]. No estoy segura de estar de acuerdo completamente, aunque fue interesante tu perspectiva.',
      explanation: 'Crea un desafío intelectual que estimule su lado competitivo'
    }, {
      type: 'Valor',
      content: 'Siempre admiré tu determinación para [logro específico]. Es una cualidad bastante única hoy en día.',
      explanation: 'Reconocimiento genuino que apela a su necesidad de respeto'
    }],
    caseStudy: {
      situation: 'Carlos, un ejecutivo de 34 años, terminó una relación de 8 meses porque sentía que su pareja "dependía demasiado de él" y "limitaba su libertad".',
      approach: 'Después de la ruptura, ella implementó un período de silencio total de 21 días, durante los cuales renovó sus redes sociales mostrando una vida social activa y nuevos intereses. Luego envió un mensaje casual mencionando un logro profesional reciente, sin pedir respuesta.',
      result: 'Carlos respondió en minutos, intrigado por su aparente transformación. La conversación fluyó naturalmente, y él propuso encontrarse. Ella mantuvo el patrón de disponibilidad limitada y misterio, lo que reactivó su instinto de "cazador". En 14 días, él estaba persiguiendo activamente una reconciliación.',
      keyLessons: ['El silencio inicial creó escasez y curiosidad', 'Mostrar una vida plena independiente de él aumentó su valor percibido', 'Mantener conversaciones estimulantes sin presión emocional', 'Espaciar encuentros para mantener el interés y deseo', 'Nunca mostrar necesidad o desesperación']
    }
  }, {
    id: 'protector',
    title: 'El Protector',
    icon: <ShieldIcon size={24} className="text-blue-500" data-id="element-768" />,
    percentage: '40%',
    description: 'Leal, estable y orientado a la familia. Valora seguridad emocional y conexión.',
    characteristics: ['Naturalmente protector y cuidadoso', 'Valora estabilidad y seguridad', 'Leal y comprometido', 'Puede ser celoso o posesivo', 'Aprecia demostraciones de vulnerabilidad'],
    detailedProfile: {
      personalityTraits: ['Leal y confiable en sus compromisos', 'Orientado a la seguridad y estabilidad', 'Tradicional en sus valores y expectativas', 'Responsable y trabajador', 'Planificador y previsor', 'Prefiere rutinas y estructuras claras', 'Protector con quienes considera importantes', 'Valora la honestidad y transparencia'],
      emotionalPatterns: ['Profundamente emocional aunque puede no expresarlo abiertamente', 'Forma vínculos emocionales fuertes y duraderos', 'Puede reprimir emociones para parecer fuerte', 'Necesita sentirse necesitado y valorado', 'Puede ser inseguro bajo su apariencia fuerte', 'Teme el abandono o la traición'],
      relationshipBehaviors: ['Busca relaciones estables y duraderas', 'Invierte significativamente en sus relaciones', 'Puede ser posesivo o celoso cuando se siente inseguro', 'Demuestra amor a través de actos de servicio y protección', 'Valora la lealtad por encima de casi todo', 'Prefiere roles tradicionales en la relación'],
      communicationStyle: ['Más reservado con sus sentimientos profundos', 'Puede tener dificultad expresando vulnerabilidad', 'Valora la comunicación honesta y directa', 'Responde bien a expresiones de aprecio y reconocimiento', 'Puede cerrarse ante críticas o conflictos', 'Prefiere conversaciones constructivas con soluciones claras']
    },
    strategy: 'Usa disparadores de vulnerabilidad y gratitud. Muestra que aprecias sus gestos de protección, pero mantén tu independencia. Valora la conexión emocional.',
    dosAndDonts: {
      dos: ['Mostrar aprecio genuino por sus gestos protectores', 'Demostrar lealtad y confiabilidad', 'Compartir vulnerabilidades de forma medida', 'Reconocer sus esfuerzos y sacrificios', 'Crear momentos de conexión emocional profunda', 'Respetar sus valores tradicionales', 'Incluirlo en decisiones importantes', 'Demostrar que valoras la estabilidad'],
      donts: ['Mostrar signos de infidelidad o deslealtad', 'Criticarlo públicamente o desafiar su rol protector', 'Ser impredecible o inestable emocionalmente', 'Rechazar sus gestos de cuidado o protección', 'Mostrar excesiva independencia o desinterés', 'Crear drama o conflictos innecesarios', 'Ignorar o minimizar sus preocupaciones', 'Cambiar planes constantemente sin considerarlo']
    },
    idealMessages: [{
      type: 'Vulnerabilidad',
      content: 'Hoy tuve un día bastante difícil en el trabajo. Siempre supe escucharme en estos momentos. Solo quería compartirlo contigo.',
      explanation: 'Muestra vulnerabilidad medida que activa su instinto protector'
    }, {
      type: 'Gratitud',
      content: 'Estaba recordando cómo me ayudaste con [situación específica]. Nunca te agradecí adecuadamente por eso. Significó mucho para mí.',
      explanation: 'Reconoce sus acciones protectoras y refuerza su rol'
    }, {
      type: 'Seguridad',
      content: 'Me encontré pensando en esos momentos tranquilos que compartíamos [menciona actividad específica]. Hay algo especial en sentirse así de cómoda con alguien.',
      explanation: 'Evoca la conexión emocional y la estabilidad que valora'
    }],
    caseStudy: {
      situation: 'Miguel, un ingeniero de 29 años, terminó una relación de 2 años porque sentía que "las cosas se habían enfriado" y que su pareja "ya no lo necesitaba como antes".',
      approach: 'Ella implementó una estrategia basada en vulnerabilidad estratégica y reconexión emocional. Después de 14 días de contacto mínimo, envió un mensaje expresando gratitud por una ocasión específica en que él la había apoyado, seguido de encuentros donde compartió desafíos personales de forma medida.',
      result: 'Miguel respondió positivamente al sentirse valorado y necesario nuevamente. La combinación de gratitud sincera y vulnerabilidad medida reactivó su instinto protector. En tres semanas, él propuso intentarlo de nuevo, esta vez con un compromiso más profundo.',
      keyLessons: ['Reconocer genuinamente sus cualidades protectoras', 'Compartir vulnerabilidades de forma estratégica sin parecer dependiente', 'Crear momentos de conexión emocional profunda', 'Demostrar lealtad y compromiso consistentemente', 'Balancear independencia con aprecio por su apoyo']
    }
  }, {
    id: 'conqueror',
    title: 'El Conquistador',
    icon: <CrownIcon size={24} className="text-yellow-500" data-id="element-769" />,
    percentage: '25%',
    description: 'Ambicioso, carismático y enfocado en estatus. Valora reconocimiento y admiración.',
    characteristics: ['Ambicioso y orientado al éxito', 'Le gusta ser admirado y reconocido', 'Valora apariencias y estatus', 'Puede ser narcisista en algunos aspectos', 'Responde bien a la admiración genuina'],
    detailedProfile: {
      personalityTraits: ['Ambicioso y orientado al logro', 'Carismático y consciente de su imagen', 'Confiado y seguro de sí mismo', 'Competitivo y busca destacarse', 'Orientado al estatus y reconocimiento social', 'Disfruta siendo el centro de atención', 'Visionario y con grandes aspiraciones', 'Puede tener rasgos narcisistas'],
      emotionalPatterns: ['Necesita validación y admiración constante', 'Puede ocultar inseguridades bajo una fachada de confianza', 'Sensible a las críticas o al rechazo', 'Busca relaciones que mejoren su imagen', 'Puede tener dificultad mostrando vulnerabilidad', 'Teme el fracaso o la pérdida de estatus'],
      relationshipBehaviors: ['Busca parejas que aumenten su estatus o imagen', 'Disfruta mostrar y "presumir" a su pareja', 'Puede ser generoso materialmente', 'Espera lealtad y admiración constante', 'Puede ser dominante en la toma de decisiones', 'Valora la apariencia y presentación social'],
      communicationStyle: ['Seguro y a veces dominante en conversaciones', 'Disfruta hablar de sus logros y planes', 'Puede monopolizar conversaciones', 'Responde positivamente a elogios y reconocimiento', 'Puede ser impaciente con temas que no le interesan', 'Prefiere comunicación que refuerce su autoestima']
    },
    strategy: 'Usa disparadores de valor y futuro. Demuestra admiración genuina por sus logros, pero mantén tu propio valor. Nunca lo menosprecies públicamente.',
    dosAndDonts: {
      dos: ['Mostrar admiración genuina por sus logros', 'Cuidar tu imagen y apariencia', 'Apoyar sus ambiciones y metas', 'Ser su "fan número uno" en público', 'Demostrar que tienes valor social propio', 'Hablar positivamente de él ante otros', 'Mostrar interés en sus proyectos y visiones', 'Reconocer sus cualidades únicas genuinamente'],
      donts: ['Criticarlo o corregirlo públicamente', 'Cuestionar sus habilidades o capacidades', 'Competir directamente por atención o reconocimiento', 'Ignorar sus logros o minimizarlos', 'Mostrar desinterés en sus ambiciones', 'Avergonzarlo o hacerlo quedar mal ante otros', 'Ser excesivamente crítica o negativa', 'Mostrar más éxito o reconocimiento que él sin validarlo']
    },
    idealMessages: [{
      type: 'Valor',
      content: 'Acabo de ver el artículo sobre tu [logro/proyecto]. Siempre admiré esa capacidad tuya para [cualidad específica]. Es realmente impresionante.',
      explanation: 'Proporciona la admiración y reconocimiento que necesita'
    }, {
      type: 'Estatus',
      content: 'Estaba en [lugar exclusivo/evento] y me acordé de ti inmediatamente. Eres de las pocas personas que apreciarían realmente un lugar así.',
      explanation: 'Asocia su identidad con exclusividad y estatus'
    }, {
      type: 'Futuro',
      content: 'Con tu visión para [proyecto/meta], estoy segura de que lograrás exactamente lo que te propones. Siempre tuviste esa claridad que pocos tienen.',
      explanation: 'Valida sus ambiciones y visión de futuro'
    }],
    caseStudy: {
      situation: 'Alejandro, un empresario de 36 años, terminó una relación de 1 año porque sentía que "ya no había admiración" y que su pareja "no entendía sus ambiciones".',
      approach: 'Ella implementó una estrategia centrada en valor y estatus. Primero mejoró su propia imagen (nuevo look, logros profesionales visibles en redes). Luego envió un mensaje reconociendo un logro reciente de él que había visto en LinkedIn, expresando admiración genuina sin pedir nada a cambio.',
      result: 'Alejandro respondió inmediatamente, complacido por el reconocimiento. Ella mantuvo un balance perfecto entre admiración y valor propio, mostrando interés en sus proyectos mientras compartía sus propios logros. En 17 días, él la invitó a un evento importante como su acompañante, reintegrándola a su círculo social.',
      keyLessons: ['La admiración genuina es un poderoso motivador', 'Mantener un equilibrio entre reconocimiento y valor propio', 'Mejorar la propia imagen aumenta el atractivo', 'Apoyar sus ambiciones sin perder identidad propia', 'Ser su "fan número uno" en público refuerza su ego positivamente']
    }
  }];
  const quizQuestions = [{
    question: '¿Cómo reacciona él cuando logra un objetivo importante?',
    options: [{
      text: 'Celebra brevemente y ya está buscando el siguiente desafío',
      type: 'hunter'
    }, {
      text: 'Comparte el logro con sus seres queridos, enfatizando cómo beneficiará a todos',
      type: 'protector'
    }, {
      text: 'Busca reconocimiento social y disfruta hablando de su éxito',
      type: 'conqueror'
    }]
  }, {
    question: '¿Qué le molesta más a él en una relación?',
    options: [{
      text: 'Sentir que pierde su independencia o libertad',
      type: 'hunter'
    }, {
      text: 'Sentir que su pareja no valora su protección o apoyo',
      type: 'protector'
    }, {
      text: 'No recibir suficiente admiración o que no reconozcan sus esfuerzos',
      type: 'conqueror'
    }]
  }, {
    question: '¿Cómo maneja él los conflictos?',
    options: [{
      text: 'Se distancia para procesar solo y puede evitar conversaciones emocionales',
      type: 'hunter'
    }, {
      text: 'Intenta resolver el problema de forma práctica, protegiendo a los involucrados',
      type: 'protector'
    }, {
      text: 'Defiende firmemente su posición y busca tener la razón',
      type: 'conqueror'
    }]
  }, {
    question: '¿Qué valora más él en una pareja?',
    options: [{
      text: 'Que tenga vida propia y respete su espacio',
      type: 'hunter'
    }, {
      text: 'Lealtad y que aprecie sus esfuerzos por cuidarla',
      type: 'protector'
    }, {
      text: 'Que lo admire y apoye sus ambiciones',
      type: 'conqueror'
    }]
  }, {
    question: '¿Cómo expresa él afecto principalmente?',
    options: [{
      text: 'A través de experiencias compartidas y acciones, no tanto con palabras',
      type: 'hunter'
    }, {
      text: 'Mediante actos de servicio, protección y apoyo constante',
      type: 'protector'
    }, {
      text: 'Con gestos visibles, regalos o llevándote a lugares exclusivos',
      type: 'conqueror'
    }]
  }, {
    question: '¿Qué tema de conversación le apasiona más a él?',
    options: [{
      text: 'Sus aventuras, desafíos superados o habilidades',
      type: 'hunter'
    }, {
      text: 'Planes a futuro, seguridad financiera o familia',
      type: 'protector'
    }, {
      text: 'Sus logros, reconocimientos o conexiones importantes',
      type: 'conqueror'
    }]
  }, {
    question: '¿Cómo reacciona él cuando se siente inseguro en la relación?',
    options: [{
      text: 'Se distancia o se muestra indiferente',
      type: 'hunter'
    }, {
      text: 'Se vuelve más protector o incluso posesivo',
      type: 'protector'
    }, {
      text: 'Busca validación o puede mencionar otras personas interesadas en él',
      type: 'conqueror'
    }]
  }, {
    question: '¿Qué aspecto de su vida cuida más él?',
    options: [{
      text: 'Su libertad e independencia',
      type: 'hunter'
    }, {
      text: 'La seguridad y bienestar de sus seres queridos',
      type: 'protector'
    }, {
      text: 'Su imagen, carrera y estatus social',
      type: 'conqueror'
    }]
  }];
  const handleTypeSelect = (typeId: string) => {
    setSelectedType(typeId);
  };
  const toggleSection = (section: string) => {
    if (expandedSection === section) {
      setExpandedSection(null);
    } else {
      setExpandedSection(section);
    }
  };
  const handleStartQuiz = () => {
    setShowQuiz(true);
    setCurrentQuestion(0);
    setAnswers({});
    setQuizCompleted(false);
    setQuizResult(null);
  };
  const handleQuizAnswer = (type: string) => {
    const newAnswers = {
      ...answers,
      [currentQuestion]: type
    };
    setAnswers(newAnswers);
    if (currentQuestion < quizQuestions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      // Calculate result
      const counts = {
        hunter: 0,
        protector: 0,
        conqueror: 0
      };
      Object.values(newAnswers).forEach(type => {
        counts[type as keyof typeof counts]++;
      });
      let result = 'hunter';
      let maxCount = counts.hunter;
      if (counts.protector > maxCount) {
        result = 'protector';
        maxCount = counts.protector;
      }
      if (counts.conqueror > maxCount) {
        result = 'conqueror';
      }
      setQuizResult(result);
      setQuizCompleted(true);
      setSelectedType(result);
    }
  };
  const renderQuizContent = () => {
    if (quizCompleted) {
      return <div className="text-center mb-8" data-id="element-770">
          <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-4" data-id="element-771">
            <CheckIcon size={32} className="text-green-600" data-id="element-772" />
          </div>
          <h3 className="text-xl font-bold text-[#2C2C2C] mb-2" data-id="element-773">
            ¡Análisis Completado!
          </h3>
          <p className="text-gray-700 mb-4" data-id="element-774">
            Basado en tus respuestas, hemos identificado el tipo masculino de tu
            pareja:
          </p>
          <div className="inline-block bg-[#9C27B0] text-white px-4 py-2 rounded-lg font-medium mb-6" data-id="element-775">
            {maleTypes.find(type => type.id === quizResult)?.title}
          </div>
          <p className="text-sm text-gray-600" data-id="element-776">
            Desplázate hacia abajo para ver el perfil detallado y estrategias
            personalizadas para reconquistarlo.
          </p>
          <button onClick={() => setShowQuiz(false)} className="mt-4 bg-[#E91E63] text-white px-4 py-2 rounded-md hover:bg-[#D81B60]" data-id="element-777">
            Ver Perfil Completo
          </button>
        </div>;
    }
    return <div className="mb-8" data-id="element-778">
        <div className="mb-4" data-id="element-779">
          <div className="flex justify-between text-sm text-gray-500 mb-2" data-id="element-780">
            <span data-id="element-781">
              Pregunta {currentQuestion + 1} de {quizQuestions.length}
            </span>
            <span data-id="element-782">
              {Math.round((currentQuestion + 1) / quizQuestions.length * 100)}
              % completado
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-783">
            <div className="bg-gradient-to-r from-[#E91E63] to-[#9C27B0] h-2 rounded-full" style={{
            width: `${(currentQuestion + 1) / quizQuestions.length * 100}%`
          }} data-id="element-784"></div>
          </div>
        </div>
        <h3 className="text-lg font-medium text-[#2C2C2C] mb-4" data-id="element-785">
          {quizQuestions[currentQuestion].question}
        </h3>
        <div className="space-y-3" data-id="element-786">
          {quizQuestions[currentQuestion].options.map((option, index) => <button key={index} className="w-full p-4 border rounded-lg text-left hover:border-[#9C27B0] hover:bg-purple-50 transition-colors" onClick={() => handleQuizAnswer(option.type)} data-id="element-787">
              {option.text}
            </button>)}
        </div>
      </div>;
  };
  return <div className="max-w-4xl mx-auto" data-id="element-788">
      <header className="mb-8 text-center" data-id="element-789">
        <h1 className="text-3xl font-bold text-[#2C2C2C] mb-2" data-id="element-790">
          Identificador de Tipo Masculino
        </h1>
        <p className="text-gray-600" data-id="element-791">
          Descubre qué tipo de hombre es tu pareja para personalizar tu
          estrategia de reconquista
        </p>
      </header>
      {showQuiz ? <div className="bg-white rounded-xl shadow-md p-6" data-id="element-792">
          <h2 className="text-xl font-semibold text-[#2C2C2C] mb-6" data-id="element-793">
            Cuestionario de Identificación de Tipo
          </h2>
          {renderQuizContent()}
        </div> : <>
          <div className="bg-white rounded-xl shadow-md p-6 mb-8" data-id="element-794">
            <div className="flex justify-between items-center mb-6" data-id="element-795">
              <h2 className="text-xl font-semibold text-[#2C2C2C]" data-id="element-796">
                Selecciona el tipo de tu pareja o realiza el cuestionario:
              </h2>
              <button onClick={handleStartQuiz} className="bg-[#9C27B0] text-white px-4 py-2 rounded-md hover:bg-[#7B1FA2] text-sm" data-id="element-797">
                Iniciar Cuestionario
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-798">
              {maleTypes.map(type => <div key={type.id} className={`border rounded-xl p-5 cursor-pointer transition-all ${selectedType === type.id ? 'border-[#E91E63] bg-pink-50' : 'border-gray-200 hover:border-gray-300'}`} onClick={() => handleTypeSelect(type.id)} data-id="element-799">
                  <div className="flex items-center justify-between mb-4" data-id="element-800">
                    <div className="flex items-center" data-id="element-801">
                      <div className="p-2 rounded-full bg-gray-100 mr-3" data-id="element-802">
                        {type.icon}
                      </div>
                      <h3 className="font-bold text-lg" data-id="element-803">{type.title}</h3>
                    </div>
                    <span className="text-sm bg-gray-100 px-2 py-1 rounded" data-id="element-804">
                      {type.percentage}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-3" data-id="element-805">
                    {type.description}
                  </p>
                  <div className="flex justify-end" data-id="element-806">
                    {selectedType === type.id && <CheckCircleIcon size={20} className="text-[#E91E63]" data-id="element-807" />}
                  </div>
                </div>)}
            </div>
            <div className="mt-6 bg-blue-50 border-l-4 border-blue-300 p-4" data-id="element-808">
              <div className="flex" data-id="element-809">
                <div className="flex-shrink-0" data-id="element-810">
                  <InfoIcon size={20} className="text-blue-600" data-id="element-811" />
                </div>
                <div className="ml-3" data-id="element-812">
                  <p className="text-sm text-blue-700" data-id="element-813">
                    ¿No estás segura del tipo de tu pareja? Utiliza el
                    cuestionario de identificación para analizar su
                    comportamiento y obtener una evaluación precisa basada en
                    patrones observables.
                  </p>
                </div>
              </div>
            </div>
          </div>
          {selectedType && <div className="bg-white rounded-xl shadow-md p-6" data-id="element-814">
              <h2 className="text-xl font-semibold text-[#2C2C2C] mb-6" data-id="element-815">
                {maleTypes.find(type => type.id === selectedType)?.title}:
                Perfil Detallado
              </h2>
              <div className="mb-6" data-id="element-816">
                <h3 className="font-medium text-[#9C27B0] mb-2" data-id="element-817">
                  Características Principales:
                </h3>
                <ul className="list-disc pl-5 space-y-1" data-id="element-818">
                  {maleTypes.find(type => type.id === selectedType)?.characteristics.map((trait, index) => <li key={index} className="text-gray-700" data-id="element-819">
                        {trait}
                      </li>)}
                </ul>
              </div>
              <div className="space-y-4 mb-8" data-id="element-820">
                {/* Personality Traits */}
                <div className="border rounded-lg overflow-hidden" data-id="element-821">
                  <button className="w-full flex justify-between items-center p-4 bg-gray-50 hover:bg-gray-100 transition-colors" onClick={() => toggleSection('personality')} data-id="element-822">
                    <span className="font-medium text-[#2C2C2C]" data-id="element-823">
                      Rasgos de Personalidad
                    </span>
                    {expandedSection === 'personality' ? <ChevronUpIcon size={20} className="text-gray-500" data-id="element-824" /> : <ChevronDownIcon size={20} className="text-gray-500" data-id="element-825" />}
                  </button>
                  {expandedSection === 'personality' && <div className="p-4 border-t" data-id="element-826">
                      <ul className="space-y-2" data-id="element-827">
                        {maleTypes.find(type => type.id === selectedType)?.detailedProfile.personalityTraits.map((trait, index) => <li key={index} className="flex items-start" data-id="element-828">
                                <div className="bg-purple-100 text-purple-700 p-1 rounded-full mr-2 mt-0.5" data-id="element-829">
                                  <span className="block h-1.5 w-1.5 rounded-full bg-current" data-id="element-830"></span>
                                </div>
                                <span className="text-gray-700" data-id="element-831">{trait}</span>
                              </li>)}
                      </ul>
                    </div>}
                </div>
                {/* Emotional Patterns */}
                <div className="border rounded-lg overflow-hidden" data-id="element-832">
                  <button className="w-full flex justify-between items-center p-4 bg-gray-50 hover:bg-gray-100 transition-colors" onClick={() => toggleSection('emotional')} data-id="element-833">
                    <span className="font-medium text-[#2C2C2C]" data-id="element-834">
                      Patrones Emocionales
                    </span>
                    {expandedSection === 'emotional' ? <ChevronUpIcon size={20} className="text-gray-500" data-id="element-835" /> : <ChevronDownIcon size={20} className="text-gray-500" data-id="element-836" />}
                  </button>
                  {expandedSection === 'emotional' && <div className="p-4 border-t" data-id="element-837">
                      <ul className="space-y-2" data-id="element-838">
                        {maleTypes.find(type => type.id === selectedType)?.detailedProfile.emotionalPatterns.map((pattern, index) => <li key={index} className="flex items-start" data-id="element-839">
                                <div className="bg-red-100 text-red-700 p-1 rounded-full mr-2 mt-0.5" data-id="element-840">
                                  <span className="block h-1.5 w-1.5 rounded-full bg-current" data-id="element-841"></span>
                                </div>
                                <span className="text-gray-700" data-id="element-842">{pattern}</span>
                              </li>)}
                      </ul>
                    </div>}
                </div>
                {/* Relationship Behaviors */}
                <div className="border rounded-lg overflow-hidden" data-id="element-843">
                  <button className="w-full flex justify-between items-center p-4 bg-gray-50 hover:bg-gray-100 transition-colors" onClick={() => toggleSection('relationship')} data-id="element-844">
                    <span className="font-medium text-[#2C2C2C]" data-id="element-845">
                      Comportamiento en Relaciones
                    </span>
                    {expandedSection === 'relationship' ? <ChevronUpIcon size={20} className="text-gray-500" data-id="element-846" /> : <ChevronDownIcon size={20} className="text-gray-500" data-id="element-847" />}
                  </button>
                  {expandedSection === 'relationship' && <div className="p-4 border-t" data-id="element-848">
                      <ul className="space-y-2" data-id="element-849">
                        {maleTypes.find(type => type.id === selectedType)?.detailedProfile.relationshipBehaviors.map((behavior, index) => <li key={index} className="flex items-start" data-id="element-850">
                                <div className="bg-pink-100 text-pink-700 p-1 rounded-full mr-2 mt-0.5" data-id="element-851">
                                  <span className="block h-1.5 w-1.5 rounded-full bg-current" data-id="element-852"></span>
                                </div>
                                <span className="text-gray-700" data-id="element-853">
                                  {behavior}
                                </span>
                              </li>)}
                      </ul>
                    </div>}
                </div>
                {/* Communication Style */}
                <div className="border rounded-lg overflow-hidden" data-id="element-854">
                  <button className="w-full flex justify-between items-center p-4 bg-gray-50 hover:bg-gray-100 transition-colors" onClick={() => toggleSection('communication')} data-id="element-855">
                    <span className="font-medium text-[#2C2C2C]" data-id="element-856">
                      Estilo de Comunicación
                    </span>
                    {expandedSection === 'communication' ? <ChevronUpIcon size={20} className="text-gray-500" data-id="element-857" /> : <ChevronDownIcon size={20} className="text-gray-500" data-id="element-858" />}
                  </button>
                  {expandedSection === 'communication' && <div className="p-4 border-t" data-id="element-859">
                      <ul className="space-y-2" data-id="element-860">
                        {maleTypes.find(type => type.id === selectedType)?.detailedProfile.communicationStyle.map((style, index) => <li key={index} className="flex items-start" data-id="element-861">
                                <div className="bg-blue-100 text-blue-700 p-1 rounded-full mr-2 mt-0.5" data-id="element-862">
                                  <span className="block h-1.5 w-1.5 rounded-full bg-current" data-id="element-863"></span>
                                </div>
                                <span className="text-gray-700" data-id="element-864">{style}</span>
                              </li>)}
                      </ul>
                    </div>}
                </div>
              </div>
              <div className="mb-8" data-id="element-865">
                <h3 className="font-medium text-[#9C27B0] mb-4" data-id="element-866">
                  Estrategia Recomendada para Reconquistarlo:
                </h3>
                <div className="bg-purple-50 border-l-4 border-purple-300 p-4 mb-6" data-id="element-867">
                  <p className="text-gray-700" data-id="element-868">
                    {maleTypes.find(type => type.id === selectedType)?.strategy}
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-869">
                  <div className="border rounded-lg p-4" data-id="element-870">
                    <h4 className="font-medium text-green-600 mb-3 flex items-center" data-id="element-871">
                      <CheckIcon size={18} className="mr-2" data-id="element-872" />
                      Qué Hacer
                    </h4>
                    <ul className="space-y-2" data-id="element-873">
                      {maleTypes.find(type => type.id === selectedType)?.dosAndDonts.dos.map((item, index) => <li key={index} className="flex items-start" data-id="element-874">
                            <div className="bg-green-100 text-green-700 p-1 rounded-full mr-2 mt-0.5" data-id="element-875">
                              <span className="block h-1.5 w-1.5 rounded-full bg-current" data-id="element-876"></span>
                            </div>
                            <span className="text-gray-700" data-id="element-877">{item}</span>
                          </li>)}
                    </ul>
                  </div>
                  <div className="border rounded-lg p-4" data-id="element-878">
                    <h4 className="font-medium text-red-600 mb-3 flex items-center" data-id="element-879">
                      <XIcon size={18} className="mr-2" data-id="element-880" />
                      Qué Evitar
                    </h4>
                    <ul className="space-y-2" data-id="element-881">
                      {maleTypes.find(type => type.id === selectedType)?.dosAndDonts.donts.map((item, index) => <li key={index} className="flex items-start" data-id="element-882">
                            <div className="bg-red-100 text-red-700 p-1 rounded-full mr-2 mt-0.5" data-id="element-883">
                              <span className="block h-1.5 w-1.5 rounded-full bg-current" data-id="element-884"></span>
                            </div>
                            <span className="text-gray-700" data-id="element-885">{item}</span>
                          </li>)}
                    </ul>
                  </div>
                </div>
              </div>
              <div className="mb-8" data-id="element-886">
                <h3 className="font-medium text-[#9C27B0] mb-4" data-id="element-887">
                  Mensajes Ideales para Este Tipo de Hombre:
                </h3>
                <div className="space-y-4" data-id="element-888">
                  {maleTypes.find(type => type.id === selectedType)?.idealMessages.map((message, index) => <div key={index} className="border rounded-lg p-4" data-id="element-889">
                        <div className="flex justify-between items-start mb-3" data-id="element-890">
                          <span className="inline-block px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full" data-id="element-891">
                            Tipo: {message.type}
                          </span>
                          <button className="text-gray-400 hover:text-[#E91E63]" onClick={() => {
                  navigator.clipboard.writeText(message.content);
                  // Show copied notification
                }} data-id="element-892">
                            <CopyIcon size={16} data-id="element-893" />
                          </button>
                        </div>
                        <p className="text-gray-700 mb-3" data-id="element-894">{message.content}</p>
                        <p className="text-xs text-gray-500 italic" data-id="element-895">
                          {message.explanation}
                        </p>
                      </div>)}
                </div>
              </div>
              <div data-id="element-896">
                <h3 className="font-medium text-[#9C27B0] mb-4" data-id="element-897">
                  Caso de Estudio: Reconquista Exitosa
                </h3>
                <div className="bg-gray-50 rounded-lg p-5" data-id="element-898">
                  <div className="mb-4" data-id="element-899">
                    <h4 className="font-medium text-[#2C2C2C] mb-1" data-id="element-900">
                      Situación:
                    </h4>
                    <p className="text-gray-700 text-sm" data-id="element-901">
                      {maleTypes.find(type => type.id === selectedType)?.caseStudy.situation}
                    </p>
                  </div>
                  <div className="mb-4" data-id="element-902">
                    <h4 className="font-medium text-[#2C2C2C] mb-1" data-id="element-903">
                      Enfoque de la Mujer:
                    </h4>
                    <p className="text-gray-700 text-sm" data-id="element-904">
                      {maleTypes.find(type => type.id === selectedType)?.caseStudy.approach}
                    </p>
                  </div>
                  <div className="mb-4" data-id="element-905">
                    <h4 className="font-medium text-[#2C2C2C] mb-1" data-id="element-906">
                      Resultado:
                    </h4>
                    <p className="text-gray-700 text-sm" data-id="element-907">
                      {maleTypes.find(type => type.id === selectedType)?.caseStudy.result}
                    </p>
                  </div>
                  <div data-id="element-908">
                    <h4 className="font-medium text-[#2C2C2C] mb-1" data-id="element-909">
                      Lecciones Clave para Ti:
                    </h4>
                    <ul className="space-y-1" data-id="element-910">
                      {maleTypes.find(type => type.id === selectedType)?.caseStudy.keyLessons.map((lesson, index) => <li key={index} className="flex items-start" data-id="element-911">
                            <div className="bg-[#E91E63] text-white p-1 rounded-full mr-2 mt-0.5" data-id="element-912">
                              <span className="block h-1 w-1 rounded-full bg-current" data-id="element-913"></span>
                            </div>
                            <span className="text-gray-700 text-sm" data-id="element-914">
                              {lesson}
                            </span>
                          </li>)}
                    </ul>
                  </div>
                </div>
              </div>
              <div className="flex justify-end mt-6" data-id="element-915">
                <button onClick={() => window.location.href = '/messages'} className="flex items-center bg-[#E91E63] text-white px-4 py-2 rounded-md hover:bg-[#D81B60]" data-id="element-916">
                  Ver Mensajes Recomendados{' '}
                  <ArrowRightIcon size={16} className="ml-2" data-id="element-917" />
                </button>
              </div>
            </div>}
        </>}
    </div>;
};
const InfoIcon = ({
  size,
  className
}: {
  size: number;
  className?: string;
}) => {
  return <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} data-id="element-918">
      <circle cx="12" cy="12" r="10" data-id="element-919"></circle>
      <line x1="12" y1="16" x2="12" y2="12" data-id="element-920"></line>
      <line x1="12" y1="8" x2="12.01" y2="8" data-id="element-921"></line>
    </svg>;
};
export default MaleTypeIdentifier;